import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { bmxurl, getAccess } from 'src/globals';
import { cExtra } from 'src/types/extra';
import { extra_returndata } from 'src/types/extra_returndata';
import { cPackage, packageObj } from 'src/types/package';
import { package_returndata } from 'src/types/package_returndata';
import { cPackageExtra } from 'src/types/packageExtra';
import { users_returndata } from 'src/types/user_returndata';
import { GlobalService } from '../_services/global.service';
import { BusyService } from '../_services/busy.service';
import { v4 } from 'uuid';

//What happened to one package, shown in the report at the end.
interface packageresult {
  name: string;
  detail: string;
  failed: boolean;
}

//One package to change, and which way.
interface packagejob {
  row: any;
  add: boolean;
}

@Component({
  selector: 'app-multipackageextras',
  templateUrl: './multipackageextras.component.html',
  styleUrls: ['./multipackageextras.component.css']
})
export class MultipackageextrasComponent implements OnInit {
  VenueID: string = localStorage.getItem("SmartVenueID")!;
  CurrencySymbol: string = localStorage.getItem("Currency") == null ? "GBP" : localStorage.getItem("Currency")!;
  httpResponse: any;

  AccessAllowed: boolean = false;
  CompletedLoading: boolean = false;

  ListExtras: cExtra[] = [];
  ListPackages: any[] = [];

  //One extra at a time. Min and Max belong to the extra being added, so they cannot be shared
  //across several extras at once - what is right for one is wrong, or not allowed, for another.
  selectedExtra: cExtra = null;

  //The ticks say which packages should sell the extra when the job is done. Choosing an extra
  //ticks the packages that already sell it, so ticking adds it and unticking takes it off.
  selectedPackages: any[] = [];

  //Min and Max for the packages the extra is added to.
  minValue: number = 0;
  maxValue: number = 1;

  confirmVisible: boolean = false;

  running: boolean = false;
  progressDone: number = 0;
  progressTotal: number = 0;

  results: packageresult[] = [];
  resultsVisible: boolean = false;

  private jobs: packagejob[] = [];

  constructor(private http: HttpClient, private router: Router, private messageService: MessageService, private globalService: GlobalService, private busyService: BusyService) {
  }

  ngOnInit(): void {

    this.globalService.setSmallScreenMenu(false);

    let userid = localStorage.getItem("SmartUserID");

    this.http.get(bmxurl + "User/" + userid).subscribe(response => {
      this.httpResponse = (response as users_returndata)
      if(this.httpResponse.succeeded)
      {
        let user = this.httpResponse.data;

        if(getAccess("PACKAGE", user)){
          this.AccessAllowed = true;

          this.getExtras();
          this.getPackages();
        }

        this.CompletedLoading = true;
      }
    }, error => {
      this.messageService.add({severity:'error', summary:'Error', detail:'Error while getting User, error:' + error.messages[0]});
    });
  }

  getExtras()
  {
    this.busyService.busy();

    this.http.get(bmxurl + "ExtraList/" + this.VenueID + "/false").subscribe(response => {
      this.busyService.idle();

      this.httpResponse = response
      if(this.httpResponse.succeeded)
      {
        this.ListExtras = ((response as extra_returndata).data as cExtra[]);
      }
      else
      {
        this.messageService.add({severity:'error', summary:'Error', detail:'Error getting Extras, error: ' + this.httpResponse.messages[0]});
      }
    }, error => {
      this.busyService.idle();
      this.messageService.add({severity:'error', summary:'Error', detail: 'Error getting Extras, error: ' + error.messages});
    });
  }

  //Only packages that are switched on, and not the ones belonging to an event, matching what the
  //Packages screen lists.
  getPackages()
  {
    this.busyService.busy();

    this.http.get(bmxurl + "packagelist/" + this.VenueID + "/false").subscribe(response => {
      this.busyService.idle();

      if((response as package_returndata).succeeded == true)
      {
        this.ListPackages = (response as package_returndata).data.filter((item: any) => item.eventid == null);

        //Ticks follow whatever the packages actually hold now.
        this.tickPackagesThatHaveIt();
      }
      else
      {
        this.messageService.add({severity:'error', summary:'Error', detail:'Error getting Packages'});
      }
    }, error => {
      this.busyService.idle();
      this.messageService.add({severity:'error', summary:'Error', detail: 'Error getting Packages, error: ' + error.messages});
    });
  }

  extraChanged()
  {
    this.results = [];
    this.resultsVisible = false;

    this.tickPackagesThatHaveIt();
  }

  private tickPackagesThatHaveIt()
  {
    if(this.selectedExtra == null)
    {
      this.selectedPackages = [];
      return;
    }

    this.selectedPackages = this.ListPackages.filter(row => this.packageHasExtra(row));
  }

  //The packages list carries each package's extras, which is how the page can tick what already
  //sells the extra without reading all 50 packages. Guarded in case a list ever comes back
  //without them: better to say so than to tick nothing and look like no package has it.
  get packagesKnowExtras(): boolean {
    return this.ListPackages.some(row => row.extras != null);
  }

  packageHasExtra(row: any): boolean {
    if(this.selectedExtra == null || row == null || row.extras == null)
    {
      return false;
    }

    return row.extras.some((existing: any) => existing.extraId == this.selectedExtra.extraId);
  }

  isTicked(row: any): boolean {
    return this.selectedPackages.some(chosen => chosen.packageId == row.packageId);
  }

  //Ticked but has not got it yet.
  get packagesToAdd(): any[] {
    if(this.selectedExtra == null)
    {
      return [];
    }

    return this.ListPackages.filter(row => this.isTicked(row) && !this.packageHasExtra(row));
  }

  //Has it, but the tick has been taken off.
  get packagesToRemove(): any[] {
    if(this.selectedExtra == null)
    {
      return [];
    }

    return this.ListPackages.filter(row => !this.isTicked(row) && this.packageHasExtra(row));
  }

  get changeCount(): number {
    return this.packagesToAdd.length + this.packagesToRemove.length;
  }

  get canApply(): boolean {
    return !this.running && this.selectedExtra != null && this.changeCount > 0;
  }

  //A Max of nothing would mean the extra could never be added to a booking.
  get chosenMax(): number {
    return this.maxValue > 0 ? this.maxValue : 1;
  }

  //Min can never be more than Max, and Max never less than 1 or less than Min. The boxes stop at
  //these, and if one is pushed past the other the other one follows rather than leaving a pair
  //that the package would refuse.
  get minBoxMax(): number {
    return this.chosenMax;
  }

  get maxBoxMin(): number {
    return this.minValue > 0 ? this.minValue : 1;
  }

  minChanged()
  {
    if(this.minValue == null || this.minValue < 0)
    {
      this.minValue = 0;
    }

    if(this.minValue > this.maxValue)
    {
      this.maxValue = this.minValue;
    }
  }

  maxChanged()
  {
    if(this.maxValue == null || this.maxValue < 1)
    {
      this.maxValue = 1;
    }

    if(this.maxValue < this.minValue)
    {
      this.minValue = this.maxValue;
    }
  }

  //The days the extra can be sold, as they read on the extra itself.
  get selectedExtraDays(): string {
    if(this.selectedExtra == null)
    {
      return "";
    }

    const days = [
      { on: this.selectedExtra.monday, name: 'Mon' },
      { on: this.selectedExtra.tuesday, name: 'Tue' },
      { on: this.selectedExtra.wednesday, name: 'Wed' },
      { on: this.selectedExtra.thursday, name: 'Thu' },
      { on: this.selectedExtra.friday, name: 'Fri' },
      { on: this.selectedExtra.saturday, name: 'Sat' },
      { on: this.selectedExtra.sunday, name: 'Sun' }
    ].filter(day => day.on).map(day => day.name);

    if(days.length == 7)
    {
      return 'Every day';
    }

    if(days.length == 0)
    {
      return 'No days';
    }

    return days.join(', ');
  }

  //The description is a required field on an extra, so it is often just the name typed again.
  get showSelectedDescription(): boolean {
    if(this.selectedExtra == null)
    {
      return false;
    }

    const description = (this.selectedExtra.description ?? "").trim().toLowerCase();

    return description != "" && description != (this.selectedExtra.name ?? "").trim().toLowerCase();
  }

  askToApply()
  {
    if(!this.canApply)
    {
      this.messageService.add({severity:'warn', summary:'Nothing to do', detail:'Choose an extra, then tick or untick the packages you want to change.'});
      return;
    }

    this.confirmVisible = true;
  }

  apply()
  {
    this.confirmVisible = false;

    this.jobs = [];
    this.packagesToAdd.forEach(row => this.jobs.push({ row: row, add: true }));
    this.packagesToRemove.forEach(row => this.jobs.push({ row: row, add: false }));

    this.results = [];
    this.resultsVisible = false;
    this.progressDone = 0;
    this.progressTotal = this.jobs.length;
    this.running = true;

    this.busyService.busy();
    this.applyToPackage(0);
  }

  //One package at a time: read it, change its extras, and save it back. Sequential rather than all
  //at once so 50 packages do not arrive at the API together, and so the count on screen means
  //something.
  private applyToPackage(index: number)
  {
    if(index >= this.jobs.length)
    {
      this.finished();
      return;
    }

    const job = this.jobs[index];

    this.http.get(bmxurl + "package/" + this.VenueID + "/" + job.row.packageId).subscribe(response => {
      this.httpResponse = (response as package_returndata);

      if(!this.httpResponse.succeeded)
      {
        this.recordResult(job.row.name, 'Could not be read, so it was left alone', true);
        this.nextPackage(index);
        return;
      }

      const packageObject = (this.httpResponse.data as cPackage);
      const detail = job.add ? this.addExtra(packageObject) : this.removeExtra(packageObject);

      if(detail.changed == 0)
      {
        this.recordResult(job.row.name, detail.text, false);
        this.nextPackage(index);
        return;
      }

      this.http.put<packageObj>(bmxurl + "/package", packageObject).subscribe(saveResponse => {
        this.httpResponse = saveResponse;

        if(this.httpResponse.succeeded)
        {
          this.recordResult(job.row.name, detail.text, false);
        }
        else
        {
          this.recordResult(job.row.name, 'Not saved: ' + this.httpResponse.messages[0], true);
        }

        this.nextPackage(index);
      }, error => {
        this.recordResult(job.row.name, 'Not saved: ' + (error?.message ?? 'an error occurred'), true);
        this.nextPackage(index);
      });
    }, error => {
      this.recordResult(job.row.name, 'Could not be read, so it was left alone', true);
      this.nextPackage(index);
    });
  }

  private nextPackage(index: number)
  {
    this.progressDone = index + 1;
    this.applyToPackage(index + 1);
  }

  //A package that already sells the extra keeps the settings it has - its own Min and Max are
  //never overwritten.
  private addExtra(packageObject: cPackage): { changed: number, text: string }
  {
    if(packageObject.extras == null)
    {
      packageObject.extras = [];
    }

    if(packageObject.extras.some(existing => existing.extraId == this.selectedExtra.extraId))
    {
      return { changed: 0, text: 'No change, it already had this extra' };
    }

    packageObject.extras.push(this.buildPackageExtra(this.selectedExtra));

    return { changed: 1, text: 'Added, Min ' + this.minValue + ' and Max ' + this.chosenMax };
  }

  private removeExtra(packageObject: cPackage): { changed: number, text: string }
  {
    if(packageObject.extras == null)
    {
      return { changed: 0, text: 'No change, it did not have this extra' };
    }

    const before = packageObject.extras.length;

    packageObject.extras = packageObject.extras.filter(existing => existing.extraId != this.selectedExtra.extraId);

    if(before == packageObject.extras.length)
    {
      return { changed: 0, text: 'No change, it did not have this extra' };
    }

    return { changed: 1, text: 'Removed' };
  }

  //The same shape the package extras screen builds when an extra is added to a single package.
  private buildPackageExtra(extra: cExtra): cPackageExtra
  {
    return {
      id: v4(),
      packageExtraId: v4(),
      extraId: extra.extraId,
      name: extra.name,
      description: extra.description,
      venueId: extra.venueId,
      monday: extra.monday,
      tuesday: extra.tuesday,
      wednesday: extra.wednesday,
      thursday: extra.thursday,
      friday: extra.friday,
      saturday: extra.saturday,
      sunday: extra.sunday,
      extraOptions: null,
      price: extra.price,
      perPerson: extra.perPerson,
      min: this.minValue,
      max: this.chosenMax,
      qty: 0,
      question: extra.question,
      answer: extra.answer,
      allparticipants: false,
      selected: false,
      venueextras: extra.venueextras,
      disabled: extra.disabled,
      imageurl: extra.imageurl
    };
  }

  private recordResult(name: string, detail: string, failed: boolean)
  {
    this.results.push({ name: name, detail: detail, failed: failed });
  }

  private finished()
  {
    this.running = false;
    this.resultsVisible = true;
    this.jobs = [];
    this.busyService.idle();

    const failed = this.results.filter(result => result.failed).length;

    if(failed > 0)
    {
      this.messageService.add({severity:'warn', summary:'Finished with problems', detail: failed + ' of ' + this.results.length + ' packages could not be saved. See the report below.'});
    }
    else
    {
      this.messageService.add({severity:'success', summary:'Finished', detail: this.results.length + (this.results.length == 1 ? ' package updated' : ' packages updated')});
    }

    //The packages have changed on the server, so read them back and let the ticks follow, rather
    //than trusting what is held here.
    this.getPackages();
  }

  get failedCount(): number {
    return this.results.filter(result => result.failed).length;
  }

  returnToList()
  {
    this.router.navigateByUrl("/package");
  }
}
